import { type ViolationDetail } from '@aysar/contracts';
import { useMemo } from 'react';

import { useDirection } from '@/shared/hooks/useDirection/useDirection';
import { formatNumber } from '@/shared/utils/format-number/format-number.util';

import { type ViolationEvidenceView } from './ViolationEvidence.types';

/** Below this a window reads better in minutes than in hours. */
const AN_HOUR = 3_600;

/**
 * Turns a case's subject into the one thing the panel renders.
 *
 * The two arms are genuinely different evidence, not two renderings of one
 * fact: a message case is somebody's words, and a behavioural case is a count
 * against a threshold over a window. Deciding which is which here rather than in
 * the component keeps the surface presentation-only (U5, W2).
 */
export function useViolationEvidenceLogic(
  subject: ViolationDetail['subject'],
): ViolationEvidenceView {
  const { locale } = useDirection();

  return useMemo<ViolationEvidenceView>(() => {
    if (subject.kind === 'message') {
      return {
        kind: 'message',
        body: subject.body,
        // An empty body is a message purged with a closed account (P19d), never
        // a message somebody sent. Rendering `''` would draw a blank panel that
        // reads as "they said nothing" — the case is still theirs to decide, and
        // the panel has to say why there is nothing there.
        isGone: subject.body === '',
        isHidden: subject.hidden,
      };
    }

    // Seconds are what the row stores and the wrong unit to show somebody
    // deciding a case: a reviewer should not be converting 3600 in their head.
    const useHours = subject.windowSeconds >= AN_HOUR;
    const count = useHours
      ? Math.round(subject.windowSeconds / AN_HOUR)
      : Math.round(subject.windowSeconds / 60);

    return {
      kind: 'behavior',
      rule: subject.rule,
      observedCount: formatNumber(subject.observedCount, locale),
      threshold: formatNumber(subject.threshold, locale),
      window: { unit: useHours ? 'hours' : 'minutes', count },
      startedAt: subject.windowStartedAt,
    };
  }, [subject, locale]);
}
